/**
 * AdvocateWidgetContext - cross-cutting widget invocation API (D-05, D-23).
 *
 * Any routed page can call useAdvocateWidget().open({ prompt, session }) to
 * imperatively open the floating Advocate chat widget with a pre-filled prompt
 * and/or a pre-loaded session. Plan 03 wires AIChatWidget to consume context
 * instead of props. Plan 06 wires TransitionTimelinePage.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export const AdvocateWidgetContext = createContext(null);

export function AdvocateWidgetProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);

  /**
   * open(options?)
   *   options.prompt - pre-fill the widget textarea (string | null | undefined)
   *   options.session - resume an existing AgentCore session (string | null | undefined)
   *
   * Only the fields that are explicitly provided are updated; omitting a field
   * leaves the existing value untouched. Passing null explicitly clears the field.
   */
  const open = useCallback((options = {}) => {
    if (options.prompt !== undefined) setInitialPrompt(options.prompt ?? null);
    if (options.session !== undefined) setCurrentSession(options.session ?? null);
    setIsOpen(true);
  }, []);

  /**
   * close() - D-06: resets isOpen, initialPrompt, and currentSession to null
   * so the next open() always starts clean unless the caller passes new values.
   */
  const close = useCallback(() => {
    setIsOpen(false);
    setInitialPrompt(null);
    setCurrentSession(null);
  }, []);

  /**
   * _setSession(sessionId) - Plan 03 internal use only.
   * Called from AIChatWidget's postMessage listener when AdvocateChatEmbed
   * posts a { type: 'session-update', sessionId } message. Updates currentSession
   * WITHOUT re-opening the widget.
   * Underscore prefix signals internal API - consumers should use open/close.
   */
  const _setSession = useCallback((sessionId) => {
    setCurrentSession(sessionId || null);
  }, []);

  const value = useMemo(
    () => ({ open, close, isOpen, currentSession, initialPrompt, _setSession }),
    [open, close, isOpen, currentSession, initialPrompt, _setSession],
  );

  return (
    <AdvocateWidgetContext.Provider value={value}>
      {children}
    </AdvocateWidgetContext.Provider>
  );
}

/**
 * useAdvocateWidget()
 *
 * Returns the widget invocation API for use in any component inside
 * <AdvocateWidgetProvider>. Safe to call outside the provider - returns
 * no-op defaults so components never crash if the tree is partially rendered
 * (e.g., during lazy-load or test isolation).
 *
 * @returns {{
 *   open: (options?: { prompt?: string|null, session?: string|null }) => void,
 *   close: () => void,
 *   isOpen: boolean,
 *   currentSession: string|null,
 *   initialPrompt: string|null,
 *   _setSession: (sessionId: string|null) => void,
 * }}
 */
export function useAdvocateWidget() {
  const ctx = useContext(AdvocateWidgetContext);
  if (!ctx) {
    // Safe fallback - hook called outside provider returns no-ops.
    // This prevents crashes during lazy loading or test isolation.
    return {
      open: () => {},
      close: () => {},
      isOpen: false,
      currentSession: null,
      initialPrompt: null,
      _setSession: () => {},
    };
  }
  return ctx;
}
